import {
  Code,
  Heading,
  Link,
  OrderedList,
  Text,
  UnorderedList,
} from '@chakra-ui/layout';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export type MarkdownRendererVariant = 'default' | 'assistant';

const ASSISTANT_TEXT_STYLE = {
  fontSize: '12px',
  lineHeight: '15px',
} as const;

export const MarkdownRenderer = ({
  content,
  variant = 'default',
}: {
  content: string | null;
  variant?: MarkdownRendererVariant;
}) => {
  const isAssistant = variant === 'assistant';

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ node: _node, ...props }) => {
          const href = props.href || '';
          if (href.startsWith('http://') || href.startsWith('https://')) {
            return (
              <Link
                color="green.600"
                rel="noopener noreferrer"
                target="_blank"
                {...props}
              />
            );
          }
          return props.children;
        },
        h1: ({ node: _node, ...props }) => (
          <Heading
            as="h1"
            size={isAssistant ? 'sm' : '2xl'}
            fontWeight="bold"
            marginY={isAssistant ? '2' : '4'}
            {...(isAssistant ? ASSISTANT_TEXT_STYLE : {})}
            {...props}
          />
        ),
        h2: ({ node: _node, ...props }) => (
          <Heading
            as="h2"
            size={isAssistant ? 'xs' : 'xl'}
            fontWeight="600"
            marginY={isAssistant ? '2' : '3'}
            {...(isAssistant ? ASSISTANT_TEXT_STYLE : {})}
            {...props}
          />
        ),
        h3: ({ node: _node, ...props }) => (
          <Heading
            as="h3"
            size={isAssistant ? 'xs' : 'lg'}
            fontWeight="600"
            marginY={isAssistant ? '2' : '3'}
            {...(isAssistant ? ASSISTANT_TEXT_STYLE : {})}
            {...props}
          />
        ),
        h4: ({ node: _node, ...props }) => (
          <Heading
            as="h4"
            size={isAssistant ? 'xs' : 'md'}
            fontWeight="600"
            marginY={isAssistant ? '2' : '3'}
            {...(isAssistant ? ASSISTANT_TEXT_STYLE : {})}
            {...props}
          />
        ),
        h5: ({ node: _node, ...props }) => (
          <Heading
            as="h5"
            size={isAssistant ? 'xs' : 'sm'}
            fontWeight="600"
            marginY={isAssistant ? '2' : '5'}
            {...(isAssistant ? ASSISTANT_TEXT_STYLE : {})}
            {...props}
          />
        ),
        p: ({ node: _node, ...props }) => (
          <Text
            fontSize={isAssistant ? '12px' : 'md'}
            lineHeight={isAssistant ? '15px' : undefined}
            marginTop="0"
            marginBottom={isAssistant ? '2' : '3'}
            {...props}
          />
        ),
        ul: ({ node: _node, ...props }) => (
          <UnorderedList
            marginBottom={isAssistant ? '2' : '3'}
            paddingLeft="2"
            spacing="1"
            fontSize={isAssistant ? '12px' : undefined}
            lineHeight={isAssistant ? '15px' : undefined}
            sx={{
              'li::marker': { color: 'var(--chakra-colors-green-500)' },
              li: { paddingInlineStart: '0.25rem' },
            }}
            {...props}
          />
        ),
        ol: ({ node: _node, ...props }) => (
          <OrderedList
            marginBottom={isAssistant ? '2' : '3'}
            fontSize={isAssistant ? '12px' : undefined}
            lineHeight={isAssistant ? '15px' : undefined}
            {...props}
          />
        ),
        strong: ({ node: _node, ...props }) => (
          <Text as="strong" fontWeight="bold" {...props} />
        ),
        code: ({ node, ...props }) => {
          const isInline =
            node?.position?.start?.line === node?.position?.end?.line;
          return (
            <Code
              colorScheme="gray"
              overflowX="auto"
              {...(!isInline && { padding: 4 })}
              {...(isInline && { transform: 'translateY(6px)' })}
              {...props}
            />
          );
        },
      }}
    >
      {content}
    </ReactMarkdown>
  );
};
